import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Settings, Users } from "lucide-react";
import { GenerateLoadingOrError } from "../../lib/utils";
import {
  useListOrganizations,
  useOrganizationMembers,
  OrganizationMember,
  OrganizationMemberPermission,
  NonClientFilter,
} from "../../lib/api/betterAuth";
import { useListCustomers } from "../../lib/api/customer";
import { DataTable } from "../../components/ui/DataTable/index";
import { Button } from "../../components/ui/button";
import { PermissionsModal } from "./components/PermissionsModal";
import { OnboardUserModal } from "./components/OnboardUserModal";
import { CustomerPermissionsModal } from "./components/CustomerPermissionsModal";
import { UpdateUserModal } from "./components/UpdateUserModal";

export interface OrgMembersTabProps {
  // "client" → filters to is_non_client=false and shows full customer/relation UI.
  // "non_client" → filters to is_non_client=true and hides customer/relation UI.
  kind: "client" | "non_client";
  title: string;
  subtitle: (orgName: string) => string;
  // When provided, restricts the per-member PermissionsModal to these permission types.
  allowedPermissionTypes?: ReadonlyArray<string>;
  // Toggles the three customer relation columns and the row-action customer button.
  showCustomerColumns: boolean;
  // Empty-state text for "no members".
  emptyMessage: string;
}

export function OrgMembersTab({
  kind,
  title,
  subtitle,
  allowedPermissionTypes,
  showCustomerColumns,
  emptyMessage,
}: OrgMembersTabProps) {
  const navigate = useNavigate();
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showOnboardUserModal, setShowOnboardUserModal] = useState(false);
  const [showUpdateUserModal, setShowUpdateUserModal] = useState(false);
  const [showCustomerPermissionsModal, setShowCustomerPermissionsModal] = useState(false);
  const [selectedMemberForCustomerPermissions, setSelectedMemberForCustomerPermissions] =
    useState<OrganizationMember | null>(null);
  const [selectedMemberForUpdate, setSelectedMemberForUpdate] = useState<OrganizationMember | null>(null);

  const nonClientFilter: NonClientFilter = kind === "client" ? "client" : "non_client";

  // Non-clients still live under the client organization.
  const organizationsRes = useListOrganizations("client");
  const clientOrg = organizationsRes?.data?.data?.[0];
  const clientOrgId = clientOrg?.id;

  const membersRes = useOrganizationMembers(clientOrgId, nonClientFilter);

  // Customers list only needed when customer UI is shown.
  const customersRes = useListCustomers({ minimal: true });

  const members = membersRes?.data?.data || [];
  const tableData = useMemo(() => {
    return members.map((member: OrganizationMember) => {
      const riskRelations = (member.relations || []).filter(
        (rel) => rel.relation_type === "risk"
      );
      const riskProfileNames = riskRelations
        .map((rel) => rel.Customer?.long_name)
        .filter((name): name is string => !!name)
        .sort();

      const freightRelations = (member.relations || []).filter(
        (rel) => rel.relation_type === "freight"
      );
      const weightRelations = (member.relations || []).filter(
        (rel) => rel.relation_type === "weight_ticket"
      );

      const freightCustomerNames = freightRelations
        .map((rel) =>
          [rel.Customer?.long_name, rel.is_implied ? "(Implied)" : undefined]
            .filter(Boolean)
            .join(" ")
        )
        .filter((name): name is string => !!name)
        .sort();
      const weightCustomerNames = weightRelations
        .map((rel) =>
          [rel.Customer?.long_name, rel.is_implied ? "(Implied)" : undefined]
            .filter(Boolean)
            .join(" ")
        )
        .filter((name): name is string => !!name)
        .sort();

      return {
        ...member,
        name: member.authuser?.name || "Unknown",
        riskProfiles: riskProfileNames.length > 0 ? riskProfileNames.join(", ") : "N/A",
        freightCustomers: freightCustomerNames.length > 0 ? freightCustomerNames.join(", ") : "N/A",
        weightTicketCustomers: weightCustomerNames.length > 0 ? weightCustomerNames.join(", ") : "N/A",
        phoneNumber: member.authuser?.phoneNumber || "No phone",
        displayRole: member.authuser?.user_id ? "Internal User" : member.role,
        permissionCount: (member.permissions || []).length,
        permissionsList:
          (member.permissions || [])
            .map((perm: OrganizationMemberPermission) =>
              perm.permission_type
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l: string) => l.toUpperCase())
            )
            .join(", ") || "None",
      };
    });
  }, [members]);

  const selectedMember = members.find((member: OrganizationMember) => member.id === selectedMemberId);

  // We only need to wait on the customers query when we're actually using customer UI.
  const requestsToTrack = showCustomerColumns
    ? [organizationsRes, membersRes, customersRes]
    : [organizationsRes, membersRes];
  const loadingOrErrorUi = GenerateLoadingOrError(requestsToTrack);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const handleRowClick = (member: OrganizationMember & { name: string; phoneNumber: string }) => {
    navigate(`/permissions/mobile/${member.id}`, {
      state: { organizationId: clientOrgId },
    });
  };

  const columns = [
    {
      id: "name",
      accessorKey: "name",
      header: "User Name",
      minWidth: 150,
      maxWidth: 200,
      cell: (info: any) => (
        <button
          className="text-left hover:text-blue-600 font-medium"
          onClick={() => {
            const member = info.row.original as OrganizationMember & {
              name: string;
              phoneNumber: string;
            };
            handleRowClick(member);
          }}
        >
          {info.getValue() as string}
        </button>
      ),
    },
    {
      id: "phoneNumber",
      accessorKey: "phoneNumber",
      header: "Phone Number",
      minWidth: 150,
      maxWidth: 200,
      cell: (info: any) => (
        <span className="text-gray-600">{info.getValue() as string}</span>
      ),
    },
    {
      id: "permissions",
      accessorKey: "permissions",
      accessorFn: (row: OrganizationMember) => {
        const permissions = row.permissions as OrganizationMemberPermission[];
        return permissions
          .map((perm: OrganizationMemberPermission) =>
            perm.permission_type
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase())
          )
          .sort()
          .join(", ");
      },
      header: "Permissions",
      size: 250,
      minWidth: 250,
      cell: (info: any) => {
        const formattedPermissions = info.getValue();
        return (
          <span className="text-sm text-gray-600">
            {formattedPermissions || (
              <span className="text-gray-400 italic">No permissions</span>
            )}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: showCustomerColumns ? 260 : 180,
      minWidth: showCustomerColumns ? 260 : 180,
      maxWidth: showCustomerColumns ? 300 : 220,
      enableFiltering: false,
      cell: (info: any) => {
        const member = info.row.original as OrganizationMember & {
          name: string;
          phoneNumber: string;
        };
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberForUpdate(member);
                setShowUpdateUserModal(true);
              }}
              title="Edit user"
              disabled={!member.authuser?.id}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberId(member.id);
                setShowPermissionsModal(true);
              }}
              title="Manage permissions"
            >
              <Settings className="h-4 w-4" />
            </Button>
            {showCustomerColumns && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMemberForCustomerPermissions(member);
                  setShowCustomerPermissionsModal(true);
                }}
                title="Manage customer permissions"
                disabled={!member.authuser?.id}
              >
                <Users className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
    ...(showCustomerColumns
      ? [
          {
            id: "riskProfiles",
            accessorKey: "riskProfiles",
            header: "Risk Profiles",
            size: 260,
            minWidth: 200,
            maxWidth: 350,
            cell: (info: any) => {
              const value = info.getValue() as string;
              return (
                <span className="text-gray-600 text-sm">
                  {value === "N/A" ? (
                    <span className="text-gray-400 italic">No risk profiles</span>
                  ) : (
                    value
                  )}
                </span>
              );
            },
          },
          {
            id: "freightCustomers",
            accessorKey: "freightCustomers",
            header: "Freight Customers",
            size: 260,
            minWidth: 200,
            maxWidth: 350,
            cell: (info: any) => {
              const value = info.getValue() as string;
              return (
                <span className="text-gray-600 text-sm">
                  {value === "N/A" ? (
                    <span className="text-gray-400 italic">No freight customers</span>
                  ) : (
                    value
                  )}
                </span>
              );
            },
          },
          {
            id: "weightTicketCustomers",
            accessorKey: "weightTicketCustomers",
            header: "Ticket Customers",
            size: 260,
            minWidth: 200,
            maxWidth: 350,
            cell: (info: any) => {
              const value = info.getValue() as string;
              return (
                <span className="text-gray-600 text-sm">
                  {value === "N/A" ? (
                    <span className="text-gray-400 italic">No weight tickets customers</span>
                  ) : (
                    value
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    {
      id: "role",
      accessorKey: "displayRole",
      header: "Role",
      minWidth: 120,
      maxWidth: 150,
      cell: (info: any) => (
        <span className="capitalize bg-gray-100 px-2 py-1 rounded-full text-xs">
          {info.getValue() as string}
        </span>
      ),
    },
  ];

  if (!clientOrg) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <div className="text-center py-8">
          <p className="text-gray-500">No client organization found.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            {title}
          </h1>
          <p className="text-gray-600 mt-2">{subtitle(clientOrg.name)}</p>
        </div>
        <Button
          onClick={() => setShowOnboardUserModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create User
        </Button>
      </div>

      {tableData.length === 0 ? (
        <div className="border rounded-lg p-8 text-center">
          <p className="text-gray-500">
            {membersRes.isLoading ? "Loading members..." : emptyMessage}
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 h-[70vh]">
          <DataTable
            columns={columns as any}
            data={tableData}
            enableVirtualization
            virtualizationRowHeight={45}
            virtualizationOverscan={2}
            enableSorting
            enableMultiSort
            enableFiltering
            stickyHeaders
            enableGrouping
            enableColumnVisibility
            enableColumnResizing
            enableColumnPinning
            useSteppedGroupingExpansion
            defaultSorting={[{ id: "name", desc: false }]}
            className="flex-1 flex flex-col min-h-0"
            tableClassName="max-h-[calc(100vh-280px)]"
          />
        </div>
      )}

      <PermissionsModal
        open={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false);
          setSelectedMemberId(null);
        }}
        member={selectedMember}
        organizationId={clientOrgId}
        allowedPermissionTypes={allowedPermissionTypes}
      />

      <OnboardUserModal
        open={showOnboardUserModal}
        onClose={() => setShowOnboardUserModal(false)}
        organizationId={clientOrgId}
        isNonClient={kind === "non_client"}
      />

      {showCustomerColumns && (
        <CustomerPermissionsModal
          open={showCustomerPermissionsModal}
          onClose={() => {
            setShowCustomerPermissionsModal(false);
            setSelectedMemberForCustomerPermissions(null);
          }}
          member={selectedMemberForCustomerPermissions}
        />
      )}

      {(showUpdateUserModal && selectedMemberForUpdate) && (
        <UpdateUserModal
          open={showUpdateUserModal}
          onClose={() => {
            setShowUpdateUserModal(false);
            setSelectedMemberForUpdate(null);
          }}
          member={selectedMemberForUpdate}
        />
      )}
    </section>
  );
}
